import React, { useState, useEffect, useCallback } from 'react';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_OPTIONS = [
  { value: 'risk_threshold', label: 'Risk above threshold' },
  { value: 'score_delta', label: 'Score delta above threshold' },
  { value: 'new_pattern', label: 'New pattern detected' },
  { value: 'funding_detected', label: 'Funding / M&A detected' },
  { value: 'leadership_change', label: 'Leadership change detected' },
];

export default function WebhookManager({ apiKey }) {
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', email: '', triggers: ['risk_threshold'], risk_threshold: 70, delta_threshold: 15, enabled: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLogs, setShowLogs] = useState(false);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(apiKey ? { 'X-API-Key': apiKey } : {}),
  }), [apiKey]);

  const loadWebhooks = useCallback(async () => {
    try {
      const [whResp, logResp] = await Promise.all([
        fetch(`${API_URL}/api/webhooks`, { headers: headers() }),
        fetch(`${API_URL}/api/webhooks/logs`, { headers: headers() }),
      ]);
      if (whResp.ok) setWebhooks(await whResp.json());
      if (logResp.ok) setLogs(await logResp.json());
    } catch (e) { setError('Failed to load webhooks'); }
  }, [headers]);

  useEffect(() => { loadWebhooks(); }, [loadWebhooks]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const url = editingId ? `${API_URL}/api/webhooks/${editingId}` : `${API_URL}/api/webhooks`;
      const method = editingId ? 'PUT' : 'POST';
      const resp = await fetch(url, { method, headers: headers(), body: JSON.stringify(form) });
      if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Failed'); }
      await loadWebhooks();
      resetForm();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/webhooks/${id}`, { method: 'DELETE', headers: headers() });
      await loadWebhooks();
    } catch (e) { setError(e.message); }
  };

  const handleToggle = async (wh) => {
    try {
      await fetch(`${API_URL}/api/webhooks/${wh.id}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ enabled: !wh.enabled }),
      });
      await loadWebhooks();
    } catch (e) { setError(e.message); }
  };

  const startEdit = (wh) => {
    setEditingId(wh.id);
    setForm({ name: wh.name, url: wh.url || '', email: wh.email || '', triggers: wh.triggers || [], risk_threshold: wh.risk_threshold || 70, delta_threshold: wh.delta_threshold || 15, enabled: wh.enabled });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: '', url: '', email: '', triggers: ['risk_threshold'], risk_threshold: 70, delta_threshold: 15, enabled: true });
    setShowForm(false);
  };

  const toggleTrigger = (value) => {
    setForm(prev => ({
      ...prev,
      triggers: prev.triggers.includes(value)
        ? prev.triggers.filter(t => t !== value)
        : [...prev.triggers, value],
    }));
  };

  return (
    <div>
      {/* Header */}
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700' }} data-testid="webhook-title">Webhooks & Alerts</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {webhooks.length} webhook(s) configured &middot; {webhooks.filter(w => w.enabled).length} active
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowForm(!showForm); }}
              data-testid="add-webhook-button">
              {showForm ? 'Cancel' : 'Add Webhook'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowLogs(!showLogs)}
              data-testid="toggle-logs-button">
              {showLogs ? 'Hide Logs' : 'View Logs'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="panel" style={{ marginBottom: '8px', borderLeft: '3px solid var(--danger)' }}>
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--danger)' }}>{error}</div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="webhook-form">
          <div className="panel-header">{editingId ? 'Edit Webhook' : 'New Webhook'}</div>
          <div className="panel-body" style={{ padding: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div className="form-group">
                <label>Name</label>
                <input type="text" placeholder="e.g. Risk Alert" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px' }} data-testid="webhook-name-input" />
              </div>
              <div className="form-group">
                <label>Webhook URL (optional)</label>
                <input type="url" placeholder="https://hooks.slack.com/..." value={form.url}
                  onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px' }} data-testid="webhook-url-input" />
              </div>
              <div className="form-group">
                <label>Email (optional)</label>
                <input type="email" placeholder="alerts@company.com" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  style={{ width: '100%', padding: '6px 8px' }} data-testid="webhook-email-input" />
              </div>
            </div>

            {/* Triggers */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Alert Triggers
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {TRIGGER_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => toggleTrigger(opt.value)}
                    className={`btn btn-sm ${form.triggers.includes(opt.value) ? 'btn-primary' : 'btn-outline'}`}
                    style={{ fontSize: '11px', padding: '3px 8px' }}
                    data-testid={`trigger-${opt.value}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Thresholds */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              {form.triggers.includes('risk_threshold') && (
                <div className="form-group">
                  <label>Risk Threshold</label>
                  <input type="number" min="0" max="100" value={form.risk_threshold}
                    onChange={e => setForm(p => ({ ...p, risk_threshold: parseInt(e.target.value) || 70 }))}
                    style={{ width: '70px', padding: '5px 8px' }} data-testid="webhook-risk-threshold" />
                </div>
              )}
              {form.triggers.includes('score_delta') && (
                <div className="form-group">
                  <label>Delta Threshold</label>
                  <input type="number" min="1" max="100" value={form.delta_threshold}
                    onChange={e => setForm(p => ({ ...p, delta_threshold: parseInt(e.target.value) || 15 }))}
                    style={{ width: '70px', padding: '5px 8px' }} data-testid="webhook-delta-threshold" />
                </div>
              )}
            </div>

            <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={loading || !form.name}
              data-testid="webhook-save-button">
              {loading ? 'Saving...' : editingId ? 'Update Webhook' : 'Create Webhook'}
            </button>
          </div>
        </div>
      )}

      {/* Webhook list */}
      {webhooks.length > 0 && (
        <div className="panel" style={{ marginBottom: '12px', overflow: 'auto' }}>
          <div className="data-table-wrap">
          <table className="data-table" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ width: '90px' }}>Status</th>
                <th style={{ width: '14%' }}>Name</th>
                <th style={{ width: '20%' }}>Destination</th>
                <th style={{ width: '22%' }}>Trigger Types</th>
                <th style={{ width: '100px' }}>Thresholds</th>
                <th style={{ width: '90px' }}>Scope</th>
                <th style={{ width: '100px' }}>Last Triggered</th>
                <th style={{ width: '90px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((wh, i) => (
                <tr key={wh.id || i} data-testid={`webhook-row-${i}`}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      data-testid={`webhook-toggle-${i}`}>
                      <div onClick={() => handleToggle(wh)}
                        style={{
                          width: '34px', height: '18px', borderRadius: '9px', cursor: 'pointer',
                          background: wh.enabled ? 'var(--success)' : 'var(--border)',
                          position: 'relative', transition: 'background 0.2s',
                        }}>
                        <div style={{
                          width: '14px', height: '14px', borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: '2px', transition: 'left 0.2s',
                          left: wh.enabled ? '18px' : '2px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: '600', color: wh.enabled ? 'var(--success)' : 'var(--text-secondary)' }}>
                        {wh.enabled ? 'ACTIVE' : 'PAUSED'}
                      </span>
                    </div>
                  </td>
                  <td style={{ fontWeight: '600' }}>{wh.name}</td>
                  <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {wh.url && <div title={wh.url}>{wh.url.length > 35 ? wh.url.slice(0, 35) + '...' : wh.url}</div>}
                    {wh.email && <div>{wh.email}</div>}
                    {!wh.url && !wh.email && <span style={{ color: 'var(--text-secondary)' }}>-</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                      {(wh.triggers || []).map((t, j) => (
                        <span key={j} className="badge badge-type" style={{ fontSize: '9px' }}>
                          {t.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ fontSize: '11px', fontFamily: "'IBM Plex Mono', monospace" }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {wh.risk_threshold ? <div>Risk &ge; {wh.risk_threshold}</div> : null}
                      {wh.delta_threshold ? <div>Score &Delta; &ge; {wh.delta_threshold}</div> : null}
                      {!wh.risk_threshold && !wh.delta_threshold && <span style={{ color: 'var(--text-secondary)' }}>-</span>}
                    </div>
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {wh.scope || 'Global'}
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {wh.last_triggered
                      ? new Date(wh.last_triggered).toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : 'Never'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-outline btn-sm" style={{ fontSize: '10px', padding: '3px 8px' }}
                        onClick={() => startEdit(wh)} data-testid={`webhook-edit-${i}`}>Edit</button>
                      <button className="btn btn-outline btn-sm" style={{ fontSize: '10px', padding: '3px 8px', color: 'var(--danger)' }}
                        onClick={() => handleDelete(wh.id)} data-testid={`webhook-delete-${i}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {webhooks.length === 0 && !showForm && (
        <div className="panel">
          <div className="panel-body" style={{ padding: '24px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>No webhooks configured</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)} data-testid="webhook-empty-add">
              Create your first webhook
            </button>
          </div>
        </div>
      )}

      {/* Webhook Logs */}
      {showLogs && (
        <div className="panel" style={{ marginTop: '12px' }} data-testid="webhook-logs-panel">
          <div className="panel-header">Recent Alert Logs</div>
          <div className="panel-body" style={{ padding: '4px 8px', maxHeight: '300px', overflow: 'auto' }}>
            {logs.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px' }}>No logs yet</div>
            ) : logs.map((log, i) => (
              <div key={i} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px' }} data-testid={`webhook-log-${i}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span style={{ fontWeight: '600' }}>{log.webhook_name || log.trigger}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                  </span>
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {log.company_name && <span>{log.company_name} &middot; </span>}
                  <span className={`badge ${log.status === 'sent' ? 'badge-high' : 'badge-low'}`} style={{ fontSize: '9px' }}>
                    {log.status || 'unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
